import { memo, useEffect } from 'react'

import api, { ApiCore, ProjectId, RegisterData } from '@monorepo/api'

enum PostRedirectEvent {
  EmailSent = 'email_sent',
  BonusApplied = 'bonus_applied',
}

const REDIRECT_EVENTS = 'redirect_events'
const AUTOLOGIN_KEY = 'autologin_token'
const SESSION_ID_OVERRIDE_KEY = 'session_id_override'
const IS_REDIRECTED_KEY = 'is_redirected'
const TRACEPARENT_KEY = 'traceparent'
const PROJECT_UUID = 'project_id'
const SITE_URL = 'site_url'
const BASE_URL_CDA = (typeof window !== 'undefined' && window.env.APP_BASE_URL_CDA) || ''

export class BaseCdaUrl {
  private static baseCdaUrl: string = this.normalizeUrl(BASE_URL_CDA)

  static get(): string {
    return this.baseCdaUrl
  }

  static set(url: string): void {
    this.baseCdaUrl = this.normalizeUrl(url)
  }

  private static normalizeUrl(url: string): string {
    if (!url) return url

    const hasProtocol = /^https?:\/\//i.test(url)

    return hasProtocol ? url : `https://${url}`
  }
}

const findTrueIndex = (arr: (boolean | null)[]) => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === null) return -1
    if (arr[i]) return i
  }

  return -1
}

async function pingDomain(urls: string[]): Promise<string> {
  const abortControllerMas = urls.map(() => new AbortController())
  const results = new Array(urls.length).fill(null)
  const CONCURRENCY_LIMIT = 2
  const TIMEOUT = 5000
  let activeRequests = 0
  let currentIndex = 0

  return new Promise((resolve, reject) => {
    const pingNextDomain = () => {
      while (activeRequests < CONCURRENCY_LIMIT && currentIndex < urls.length) {
        const index = currentIndex++
        activeRequests++

        const controller = abortControllerMas[index]
        const timeoutId = setTimeout(() => {
          controller?.abort()
        }, TIMEOUT)

        fetch(
          `https://www.${urls[index]!.replace(
            /^www\./,
            '',
          )}/internal/gateway/ping/?browser_client=telegram`,
          {
            signal: controller?.signal,
            mode: 'no-cors',
            cache: 'no-store',
          },
        )
          .then(() => (results[index] = true))
          .catch(() => (results[index] = false))
          .finally(() => {
            clearTimeout(timeoutId)
            activeRequests--
            const findIndexUrl = findTrueIndex(results)

            if (~findIndexUrl) {
              results.forEach((res, idx) => {
                if (res === null) abortControllerMas[idx]?.abort()
              })

              return resolve(urls[findIndexUrl]!)
            }

            if (results.every((res) => res === false)) {
              return reject(false)
            }

            pingNextDomain()
          })
      }
    }

    pingNextDomain()
  })
}

interface TRedirectOptions {
  autologin?: string
  traceparent?: string
  sessionId?: string | number
  account_registered_by?: ApiCore.PublicAccountRegistrationTypeEnum
  user_bonuses?: ApiCore.RegisteredUserBonusInfoSchema[]
}

function redirectToTarget(url: string, options?: TRedirectOptions) {
  const { autologin, sessionId, traceparent, account_registered_by, user_bonuses } = options || {}
  const newUrl = new URL(url)
  const currentSearchParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentSearchParams.entries()) newUrl.searchParams.set(key, value)

  newUrl.searchParams.set(IS_REDIRECTED_KEY, 'true')
  if (autologin) newUrl.searchParams.set(AUTOLOGIN_KEY, autologin)
  if (sessionId) newUrl.searchParams.set(SESSION_ID_OVERRIDE_KEY, String(sessionId))
  if (traceparent) newUrl.searchParams.set(TRACEPARENT_KEY, traceparent)

  const events: PostRedirectEvent[] = []
  if (account_registered_by === ApiCore.PublicAccountRegistrationTypeEnum.Email)
    events.push(PostRedirectEvent.EmailSent)
  if (user_bonuses?.length) events.push(PostRedirectEvent.BonusApplied)
  if (events.length) newUrl.searchParams.set(REDIRECT_EVENTS, events.join(','))
  newUrl.searchParams.set(
    'reg_bonus_id',
    user_bonuses?.[0]?.bonus_id ? String(user_bonuses[0].bonus_id) : '1',
  )
  newUrl.searchParams.set('dep', 'true')
  window.location.href = newUrl.toString()
}

function redirectToCdaDomain(
  url: string,
  { autologin, sessionId, traceparent, account_registered_by, user_bonuses }: TRedirectOptions,
) {
  const siteUrl = new URL(url)
  const currentSearchParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentSearchParams.entries()) siteUrl.searchParams.set(key, value)
  const events: PostRedirectEvent[] = []
  if (account_registered_by === ApiCore.PublicAccountRegistrationTypeEnum.Email)
    events.push(PostRedirectEvent.EmailSent)
  if (user_bonuses?.length) events.push(PostRedirectEvent.BonusApplied)
  if (events.length) siteUrl.searchParams.set(REDIRECT_EVENTS, events.join(','))
  siteUrl.searchParams.set('dep', 'true')
  siteUrl.searchParams.set(
    'reg_bonus_id',
    user_bonuses?.[0]?.bonus_id ? String(user_bonuses[0].bonus_id) : '1',
  )

  const newUrl = new URL(`${BaseCdaUrl.get()}/cda/public/v2`)
  newUrl.searchParams.set(SITE_URL, encodeURIComponent(siteUrl.href))
  newUrl.searchParams.set('from_landing', 'true')
  const projectId = ProjectId.get()
  if (projectId) newUrl.searchParams.set(PROJECT_UUID, projectId)
  if (autologin) newUrl.searchParams.set(AUTOLOGIN_KEY, autologin)
  if (sessionId) newUrl.searchParams.set(SESSION_ID_OVERRIDE_KEY, String(sessionId))
  if (traceparent) newUrl.searchParams.set(TRACEPARENT_KEY, traceparent)
  window.location.href = newUrl.toString()
}

async function pingDomainRedirect({
  domains,
  traceparent,
  autologin_token,
  session_id_override,
  account_registered_by,
  user_bonuses,
  language,
}: RegisterData) {
  const domainRedirect = await pingDomain(domains).catch(() => domains[0])

  const isCdaPingOk = await fetch(BaseCdaUrl.get(), { method: 'HEAD', mode: 'no-cors' })
    .then(() => true)
    .catch(() => false)

  const redirectFn = isCdaPingOk ? redirectToCdaDomain : redirectToTarget
  let redirectUrl = `https://www.${domainRedirect.replace(/^www\./, '')}`
  if (language) redirectUrl += `/${language}`
  const redirectParams: TRedirectOptions = {
    autologin: autologin_token,
    sessionId: session_id_override,
    account_registered_by,
    traceparent,
    user_bonuses,
  }

  redirectFn(redirectUrl, redirectParams)
}

interface Props {
  dataRegister: RegisterData | null
  setDataRegister?: (value: RegisterData | null) => void
  setIsCdaPending?: (value: boolean) => void
  setIsCdaCheck?: (value: boolean) => void
  setIsCdaBalancerInfoLoading?: (value: boolean) => void
  setCdaBalancerInfo?: (value: ApiCore.CDALandingsResponse | null) => void
}

const CDA: React.FC<React.PropsWithChildren & Props> = ({
  children,
  dataRegister,
  setDataRegister,
  setIsCdaPending,
  setIsCdaCheck,
  setIsCdaBalancerInfoLoading,
  setCdaBalancerInfo,
}) => {
  useEffect(() => {
    api.core
      .cdaDomainsRetrieve({ credentials: 'include', cache: 'no-store' })
      .then(({ data }) => {
        setDataRegister?.(data)
      })
      .catch((e) => {
        console.warn('cda/check failed:', e)
        setIsCdaCheck?.(true)
      })
    setIsCdaBalancerInfoLoading?.(true)
    api.cda
      .publicCdaV2LandingsRetrieve({ project_id: ProjectId.get() })
      .then(({ data }) => setCdaBalancerInfo?.(data))
      .catch((e) => {
        console.warn('cda/balancer info failed:', e)
        setCdaBalancerInfo?.(null)
      })
      .finally(() => setIsCdaBalancerInfoLoading?.(false))
  }, [])

  useEffect(() => {
    if (dataRegister) {
      setIsCdaPending?.(true)
      pingDomainRedirect(dataRegister)
    }
  }, [dataRegister, setIsCdaPending])

  return children
}

export default memo(CDA)
